import { toast } from 'react-toastify';

/**
 * Gestionnaire d'erreurs centralisé pour les appels API
 */
export class ErrorHandler {
  /**
   * Gère les erreurs API et affiche les notifications appropriées
   * @param {Error} error - L'erreur à gérer
   * @param {Object} options - Options de gestion
   * @param {boolean} options.showToast - Afficher une notification toast (défaut: true)
   * @param {string} options.defaultMessage - Message par défaut si aucun message d'erreur
   * @param {Function} options.onValidationError - Callback pour les erreurs de validation
   * @param {Function} options.onAuthError - Callback pour les erreurs d'authentification
   */
  static handle(error, options = {}) {
    const {
      showToast = true,
      defaultMessage = 'Une erreur est survenue',
      onValidationError,
      onAuthError,
    } = options;

    const fieldLabels = {
      current_password: 'Mot de passe actuel',
      password: 'Nouveau mot de passe',
      password_confirmation: 'Confirmation du mot de passe',
      email: 'Adresse email',
      phone: 'Téléphone',
      mobile_money_phone: 'Téléphone mobile money',
      address: 'Adresse',
      username: "Nom d'utilisateur",
      name: 'Nom',
      firstname: 'Prénom',
      first_name: 'Prénom',
      last_name: 'Nom',
      gender: 'Civilité',
    };

    // Erreur de validation (422)
    if (error.status === 422 && error.validationErrors) {
      if (onValidationError) {
        onValidationError(error.validationErrors);
      }

      if (showToast) {
        Object.entries(error.validationErrors).forEach(([field, messages]) => {
          messages.forEach(msg => {
            const label = fieldLabels[field] || field;
            toast.error(`${label} : ${msg}`);
          });
        });
      }

      return {
        type: 'validation',
        errors: error.validationErrors,
        message: error.message || 'Erreur de validation',
      };
    }

    // Erreur d'authentification (401)
    if (error.status === 401) {
      if (onAuthError) {
        onAuthError();
      }

      if (showToast) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      }

      return {
        type: 'authentication',
        message: 'Non authentifié',
      };
    }

    // Erreur d'autorisation (403)
    if (error.status === 403) {
      if (showToast) {
        toast.error('Vous n\'avez pas les permissions nécessaires.');
      }

      return {
        type: 'authorization',
        message: 'Accès refusé',
      };
    }

    // Erreur 404
    if (error.status === 404) {
      if (showToast) {
        toast.error('Ressource non trouvée.');
      }

      return {
        type: 'not_found',
        message: 'Ressource non trouvée',
      };
    }

    // Rate-limit (429)
    if (error.status === 429) {
      const message = 'Trop de requêtes, réessaie dans un instant.';
      if (showToast) {
        toast.error(message);
      }

      return {
        type: 'rate_limit',
        message,
      };
    }

    // Erreur serveur (500+)
    if (error.status >= 500) {
      if (showToast) {
        toast.error('Erreur serveur. Veuillez réessayer plus tard.');
      }

      return {
        type: 'server',
        message: 'Erreur serveur',
      };
    }

    // Erreur réseau
    if (error.message && error.message.includes('réseau')) {
      if (showToast) {
        toast.error('Erreur de connexion. Vérifiez votre connexion internet.');
      }

      return {
        type: 'network',
        message: 'Erreur réseau',
      };
    }

    // Erreur générique
    if (showToast) {
      toast.error(error.message || defaultMessage);
    }

    return {
      type: 'generic',
      message: error.message || defaultMessage,
    };
  }

  /**
   * Extrait les messages d'erreur de validation dans un format simple
   * @param {Object} validationErrors - Erreurs de validation de l'API
   * @returns {Object} - Objet avec les champs et leurs premiers messages d'erreur
   */
  static extractValidationMessages(validationErrors) {
    const messages = {};
    Object.entries(validationErrors).forEach(([field, errors]) => {
      messages[field] = Array.isArray(errors) ? errors[0] : errors;
    });
    return messages;
  }

  /**
   * Vérifie si une erreur est une erreur de validation
   * @param {Error} error - L'erreur à vérifier
   * @returns {boolean}
   */
  static isValidationError(error) {
    return error.status === 422 && error.validationErrors;
  }

  /**
   * Vérifie si une erreur est une erreur d'authentification
   * @param {Error} error - L'erreur à vérifier
   * @returns {boolean}
   */
  static isAuthError(error) {
    return error.status === 401;
  }

  /**
   * Vérifie si une erreur est une erreur d'autorisation
   * @param {Error} error - L'erreur à vérifier
   * @returns {boolean}
   */
  static isAuthorizationError(error) {
    return error.status === 403;
  }
}

/**
 * Hook pour gérer les erreurs dans les composants
 */
export const useErrorHandler = () => {
  const handleError = (error, options = {}) => {
    return ErrorHandler.handle(error, options);
  };

  return { handleError };
};

export default ErrorHandler;
